/**
 * Discipline coach settings: per account, everything off until the user turns the coach on.
 *
 * Stored in their own synced document, `settings/coach` (field merge per top-level key), one
 * top-level key per account (`acct:` + `coachAccountKey(loginId, accountId)`), so edits to two
 * accounts on two devices merge instead of overwriting each other. `settings/main` is untouched.
 * A missing, malformed or newer-version entry reads as OFF and is never rewritten by reading;
 * unknown keys (a newer client's) are preserved.
 *
 * Thresholds default to the owner's spec (COACH-RULES.md §0.1 and the rule triggers). They are
 * coach thresholds only: they never change a HELM risk limit (safety constraint 5).
 */
import type { DocSync } from "../sync/client.ts";
import { SyncedDocStore } from "../sync/docStore.ts";
import type { KeyValueStorage } from "../workspacesSync.ts";
import { COACH_RULE_IDS, coachAccountKey, type CoachRuleId, type CoachTone } from "./types.ts";

export const COACH_DOC_ID = "coach";
export const COACH_SETTINGS_VERSION = 1;

export type CoachThresholds = {
  /** MAX_TRADES: DON'T OVERTRADE at MAX−1 entries, TRADE LIMIT HIT at MAX. */
  maxTrades: number;
  lostCountEntries: number;
  lostCountMinutes: number;
  tickerHopSymbols: number;
  tickerHopMinutes: number;
  flipReversals: number;
  flipMinutes: number;
  stepAwayActions: number;
  stepAwayMinutes: number;
  /** WHERE'S YOUR STOP: no protective stop this long after the entry. */
  stopGraceSeconds: number;
  /** EXIT LATER: a stop but no target this long after the entry. */
  targetGraceSeconds: number;
  /** LET PRICE COME TO YOUR LEVEL: resting limit cancelled → market same side within this. */
  chaseSeconds: number;
  /** WINDOW CLOSING: this long before the session window ends. */
  closingMinutes: number;
  /** Per-message cooldown (§0.2). */
  cooldownMinutes: number;
};

export type CoachWindow = {
  enabled: boolean;
  /** "HH:MM" wall clock in `zone`; a window may run past midnight (start > end). */
  start: string;
  end: string;
  zone: string;
};

export type CoachReminders = {
  enabled: boolean;
  intervalMinutes: number;
  /** The user's own lines, shown after the built-in ones. */
  custom: string[];
};

export type CoachAccountSettings = {
  v: typeof COACH_SETTINGS_VERSION;
  /** Master switch. Off by default: nothing is evaluated or shown for this account. */
  enabled: boolean;
  tone: CoachTone;
  reminders: CoachReminders;
  window: CoachWindow;
  thresholds: CoachThresholds;
  /** Each rule can be switched off on its own (all on once the coach is on). */
  rules: Record<CoachRuleId, boolean>;
};

export const DEFAULT_COACH_THRESHOLDS: CoachThresholds = {
  maxTrades: 6,
  lostCountEntries: 4,
  lostCountMinutes: 20,
  tickerHopSymbols: 3,
  tickerHopMinutes: 30,
  flipReversals: 3,
  flipMinutes: 20,
  stepAwayActions: 10,
  stepAwayMinutes: 5,
  stopGraceSeconds: 15,
  targetGraceSeconds: 30,
  chaseSeconds: 60,
  closingMinutes: 10,
  cooldownMinutes: 20,
};

/** Bounds for each threshold (inclusive); out-of-range values are clamped on read. */
export const COACH_THRESHOLD_BOUNDS: Readonly<Record<keyof CoachThresholds, readonly [number, number]>> = {
  maxTrades: [1, 100],
  lostCountEntries: [2, 50],
  lostCountMinutes: [1, 240],
  tickerHopSymbols: [2, 20],
  tickerHopMinutes: [1, 240],
  flipReversals: [1, 50],
  flipMinutes: [1, 240],
  stepAwayActions: [2, 200],
  stepAwayMinutes: [1, 60],
  stopGraceSeconds: [1, 600],
  targetGraceSeconds: [1, 600],
  chaseSeconds: [5, 600],
  closingMinutes: [1, 120],
  cooldownMinutes: [1, 240],
};

export const REMINDER_INTERVAL_BOUNDS = [1, 240] as const;
export const MAX_CUSTOM_REMINDERS = 20;
export const MAX_REMINDER_LENGTH = 120;

function allRules(on: boolean): Record<CoachRuleId, boolean> {
  const r = {} as Record<CoachRuleId, boolean>;
  for (const id of COACH_RULE_IDS) r[id] = on;
  return r;
}

/** A fresh account's settings: the coach OFF; rules, reminders and the spec's thresholds ready. */
export function defaultCoachSettings(): CoachAccountSettings {
  return {
    v: COACH_SETTINGS_VERSION,
    enabled: false,
    tone: "coach",
    reminders: { enabled: true, intervalMinutes: 10, custom: [] },
    window: { enabled: false, start: "08:30", end: "11:00", zone: "America/Chicago" },
    thresholds: { ...DEFAULT_COACH_THRESHOLDS },
    rules: allRules(true),
  };
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function clampInt(v: unknown, [lo, hi]: readonly [number, number], fallback: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return fallback;
  return Math.min(hi, Math.max(lo, Math.round(v)));
}

/**
 * Read one account's entry. Anything but a literal version-1 object reads as the defaults
 * (coach OFF): a newer client's entry is never half-understood into "on".
 */
export function sanitizeCoachSettings(raw: unknown): CoachAccountSettings {
  const d = defaultCoachSettings();
  if (!isRecord(raw) || raw["v"] !== COACH_SETTINGS_VERSION) return d;
  const tone = raw["tone"];
  const rem = isRecord(raw["reminders"]) ? raw["reminders"] : {};
  const win = isRecord(raw["window"]) ? raw["window"] : {};
  const th = isRecord(raw["thresholds"]) ? raw["thresholds"] : {};
  const rules = isRecord(raw["rules"]) ? raw["rules"] : {};
  const thresholds = { ...d.thresholds };
  for (const k of Object.keys(DEFAULT_COACH_THRESHOLDS) as (keyof CoachThresholds)[]) {
    thresholds[k] = clampInt(th[k], COACH_THRESHOLD_BOUNDS[k], DEFAULT_COACH_THRESHOLDS[k]);
  }
  const ruleFlags = allRules(true);
  for (const id of COACH_RULE_IDS) if (rules[id] === false) ruleFlags[id] = false;
  const custom = Array.isArray(rem["custom"])
    ? rem["custom"]
        .filter((s): s is string => typeof s === "string")
        .map((s) => s.trim().slice(0, MAX_REMINDER_LENGTH))
        .filter((s) => s.length > 0)
        .slice(0, MAX_CUSTOM_REMINDERS)
    : [];
  const start = typeof win["start"] === "string" && HHMM.test(win["start"]) ? win["start"] : d.window.start;
  const end = typeof win["end"] === "string" && HHMM.test(win["end"]) ? win["end"] : d.window.end;
  return {
    v: COACH_SETTINGS_VERSION,
    enabled: raw["enabled"] === true,
    tone: tone === "calm" || tone === "blunt" || tone === "coach" ? tone : "coach",
    reminders: {
      enabled: rem["enabled"] !== false,
      intervalMinutes: clampInt(
        rem["intervalMinutes"],
        REMINDER_INTERVAL_BOUNDS,
        d.reminders.intervalMinutes,
      ),
      custom,
    },
    window: {
      enabled: win["enabled"] === true && start !== end,
      start,
      end,
      zone: typeof win["zone"] === "string" && win["zone"].length > 0 ? win["zone"] : d.window.zone,
    },
    thresholds,
    rules: ruleFlags,
  };
}

// ---- the document ---------------------------------------------------------------------------

export type CoachDoc = Record<string, unknown>;

/** The document key of one account's entry. */
export function coachDocKey(loginId: string, accountId: string): string {
  return `acct:${coachAccountKey(loginId, accountId)}`;
}

/**
 * Is this entry's coach ON? The same answer as `sanitizeCoachSettings(raw).enabled`, without
 * parsing the rest: the always-loaded shell uses it to decide whether to load the coach at all.
 */
export function coachEntryEnabled(raw: unknown): boolean {
  return isRecord(raw) && raw["v"] === COACH_SETTINGS_VERSION && raw["enabled"] === true;
}

/** Is the coach on for this (login, account)? */
export function coachEnabledIn(doc: CoachDoc, loginId: string, accountId: string): boolean {
  return coachEntryEnabled(doc[coachDocKey(loginId, accountId)]);
}

/** Is the coach on for any account in the document? */
export function anyCoachEnabled(doc: CoachDoc): boolean {
  for (const [k, v] of Object.entries(doc)) if (k.startsWith("acct:") && coachEntryEnabled(v)) return true;
  return false;
}

/** One account's settings from the document (defaults, i.e. OFF, when absent or unreadable). */
export function coachSettingsOf(doc: CoachDoc, loginId: string, accountId: string): CoachAccountSettings {
  return sanitizeCoachSettings(doc[coachDocKey(loginId, accountId)]);
}

/** The document with one account's entry replaced; every other key is kept as it was. */
export function withCoachSettings(
  doc: CoachDoc,
  loginId: string,
  accountId: string,
  settings: CoachAccountSettings,
): CoachDoc {
  return { ...doc, [coachDocKey(loginId, accountId)]: sanitizeCoachSettings(settings) };
}

/** Untrusted server/local body → document. Unknown keys are kept (a newer client's entries). */
export function sanitizeCoachDoc(raw: unknown): CoachDoc | null {
  return isRecord(raw) ? { ...raw } : null;
}

export type CoachSettingsStore = SyncedDocStore<CoachDoc>;

/** `settings/coach`, synced per user and cached in local storage (see sync/docStore.ts). */
export function createCoachSettingsStore(opts: {
  storageKey: string;
  storage: KeyValueStorage;
  sync: boolean;
  docs?: DocSync;
  saveDelayMs?: number;
}): CoachSettingsStore {
  return new SyncedDocStore<CoachDoc>({
    docType: "settings",
    docId: COACH_DOC_ID,
    sanitize: sanitizeCoachDoc,
    seed: () => ({}),
    ...opts,
  });
}
